// ======================================================================
// FILE:
// src/modules/expense_management/claims/pages/EditExpenseClaimPage.jsx
//
// MODULE:
// Expense Management
//
// FEATURE:
// Edit Expense Claim
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import ExpenseClaimHeader
from "../components/ExpenseClaimHeader";

import ExpenseItemsGrid
from "../components/ExpenseItemsGrid";

import ExpenseSummaryCard
from "../components/ExpenseSummaryCard";

import AttachmentPanel
from "../components/AttachmentPanel";

import CommentPanel
from "../components/CommentPanel";

import WorkflowPanel
from "../components/WorkflowPanel";

import expenseClaimService
from "../services/expenseClaimService";

import businessReferenceService
from "../services/businessReferenceService";

import validateExpenseClaim
from "../utils/expenseClaimValidation"


import BusinessReferenceCard
from "../components/BusinessReferenceCard";

export default function EditExpenseClaimPage() {

  const navigate =
    useNavigate();

  const { id } =
    useParams();

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  // ==========================================================
  // HEADER
  // ==========================================================

  const [header, setHeader] =
    useState({});

  // ==========================================================
  // ITEMS
  // ==========================================================

  const [items, setItems] =
    useState([]);

  // ==========================================================
  // ATTACHMENTS
  // ==========================================================

  const [attachments, setAttachments] =
    useState([]);

  // ==========================================================
  // COMMENTS
  // ==========================================================

  const [comments, setComments] =
    useState([]);

  // ==========================================================
  // WORKFLOW
  // ==========================================================

  const [workflow, setWorkflow] =
    useState([]);

  // ==========================================================
  // INITIAL LOAD
  // ==========================================================

  useEffect(() => {

    if (id) {

      loadClaim();

    }

  }, [id]);

  // ==========================================================
  // LOAD CLAIM
  // ==========================================================

  async function loadClaim() {

    try {

      setLoading(true);

      const claim =
          await expenseClaimService
              .getExpenseClaim(id);

      let headerData = claim || {};

      if (

          claim?.reference_id &&

          claim?.expense_source &&

          claim?.reference_type

      ) {

          try {

              const details =
                  await businessReferenceService
                      .getBusinessReferenceDetails(

                          claim.reference_id,

                          claim.expense_source,

                          claim.reference_type

                      );
              console.log("Business Reference Details", details);

              headerData = {

                  ...headerData,

                  ...details,

              };

          }

          catch (error) {

              console.error(

                  "Unable to load Business Reference Details",

                  error

              );

          }

      }

      setHeader(headerData);


      const claimItems =
        await expenseClaimService
          .getExpenseClaimItems(id);
      
      console.log(
          "Expense Claim",
          claim
      );

      setItems(

        (claimItems || []).map(item => ({

          ...item,

          id:
            item.expense_claim_item_id,

          isEditing: false,

        }))

      );

      const attachmentRows =
        await expenseClaimService
          .getExpenseAttachments(id);

      setAttachments(
        attachmentRows || []
      );

      const commentRows =
        await expenseClaimService
          .getExpenseComments(id);

      setComments(
        commentRows || []
      );

      const historyRows =
        await expenseClaimService
          .getExpenseHistory(id);

      setWorkflow(
        historyRows || []
      );

    }

    catch (error) {

      console.error(error);

      alert(
        "Unable to load Expense Claim."
      );

      navigate(
        "/expense-management/claims"
      );

    }

    finally {

      setLoading(false);

    }

  }

  // ==========================================================
  // BUILD PAYLOAD
  // ==========================================================

  function buildPayload() {

  return {

    employee_id:
      header.employee_id,

    expense_policy_id:
      header.expense_policy_id,

    currency_id:
      header.currency_id,

    claim_date:
      header.claim_date,

    title:
      header.title,

    purpose:
      header.purpose,

    items: items.map(item => ({

      expense_claim_item_id:
        item.expense_claim_item_id,

      expense_date:
        item.expense_date,

      expense_category_id:
        item.expense_category_id,

      expense_type_id:
        item.expense_type_id,

      vendor_id:
        item.vendor_id,

      project_id:
        item.project_id,

      cost_center_id:
        item.cost_center_id,

      payment_mode_id:
        item.payment_mode_id,

      receipt_type_id:
        item.receipt_type_id,

      expense_reason_id:
        item.expense_reason_id,

      tax_id:
        item.tax_id,

      description:
        item.description,

      quantity:
        item.quantity || 1,

      unit_price:
        item.unit_price || 0,

      amount:
        item.amount || 0,

      tax_amount:
        item.tax_amount || 0,

      total_amount:
        item.total_amount || 0,

      currency_id:
        header.currency_id,

      bill_number:
        item.bill_number,

      bill_date:
        item.bill_date,

      remarks:
        item.remarks

    }))

  };

}
    // ==========================================================
  // SAVE DRAFT
  // ==========================================================

  async function handleSaveDraft() {

    try {

      const validation =
        validateExpenseClaim({

            header,
            items,
            attachments,

        });

    if (!validation.isValid) {

        alert(

            validation.errors.join("\n")

        );

        return;

    }
      
      setSaving(true);

      const payload =         buildPayload();
      console.log(
          "UPDATE PAYLOAD",
          JSON.stringify(payload, null, 2)
      );

      await expenseClaimService
        .updateExpenseClaim(
          id,
          payload
        );

      alert(
        "Expense Claim updated successfully."
      );

      navigate(
        "/expense-management/claims"
      );

    }

    catch (error) {

      console.error(error);

      alert(

        error?.response?.data?.message ||

        "Unable to update expense claim."

      );

    }

    finally {

      setSaving(false);

    }

  }

  // ==========================================================
  // SUBMIT
  // ==========================================================

  async function handleSubmit() {

  try {

    const validation = validateExpenseClaim({
      header,
      items,
      attachments,
    });

    if (!validation.isValid) {
      alert(validation.errors.join("\n"));
      return;
    }

    setSaving(true);

    const payload = buildPayload();

    // Save latest changes
    await expenseClaimService.updateExpenseClaim(
      id,
      payload
    );

    // Submit claim
    await expenseClaimService.submitExpenseClaim(id);

      alert("Expense Claim submitted successfully.");

      navigate("/expense-management/claims");

    } catch (error) {

      console.error(error);

      alert(
        error?.response?.data?.message ||
        "Unable to submit expense claim."
      );

    } finally {

      setSaving(false);

    }

  }

  // ==========================================================
  // CANCEL
  // ==========================================================

  function handleCancel() {

    navigate(
      "/expense-management/claims"
    );

  }

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {

    return (

      <div className="container-fluid">

        <div className="text-center py-5">

          <div className="spinner-border text-primary" />

        </div>

      </div>

    );

  }

  // ==========================================================
  // RENDER
  // ==========================================================

  return (

    <div className="container-fluid">

      {/* ====================================================== */}
      {/* HEADER */}
      {/* ====================================================== */}

      <div
        className="card border-0 mb-4"
        style={{

          background:
            "linear-gradient(90deg,#0d6efd,#4f8cff)",

          borderRadius: 12,

          color: "#fff",

        }}
      >

        <div className="card-body">

          <h2 className="mb-1">

            Edit Expense Claim

          </h2>

          <div>

            Update existing employee
            expense claim

          </div>

        </div>

      </div>

      
      {/* ====================================================== */}
      {/* CLAIM HEADER */}
      {/* ====================================================== */}

      <ExpenseClaimHeader

        data={header}

        onChange={setHeader}

      />

      {/* ====================================================== */}
      {/* BUSINESS REFERENCE */}
      {/* ====================================================== */}

      <BusinessReferenceCard

          data={header}

          onChange={setHeader}

      />
      {/* ====================================================== */}
      {/* EXPENSE ITEMS */}
      {/* ====================================================== */}

      <ExpenseItemsGrid

        rows={items}

        onChange={setItems}

      />

      {/* ====================================================== */}
      {/* SUMMARY */}
      {/* ====================================================== */}

      <ExpenseSummaryCard

        items={items}

      />

      {/* ====================================================== */}
      {/* ATTACHMENTS */}
      {/* ====================================================== */}

      <AttachmentPanel

        attachments={attachments}

        onChange={setAttachments}

      />

      {/* ====================================================== */}
      {/* COMMENTS */}
      {/* ====================================================== */}

      <CommentPanel

        comments={comments}

        onChange={setComments}

      />

      {/* ====================================================== */}
      {/* WORKFLOW */}
      {/* ====================================================== */}

      <WorkflowPanel

        rows={workflow}

      />
            {/* ====================================================== */}
      {/* ACTIONS */}
      {/* ====================================================== */}

      <div className="card mt-4">

        <div
          className="
            card-body
            d-flex
            justify-content-end
            gap-2
          "
        >

          <button
            className="
              btn
              btn-outline-secondary
            "
            onClick={
              handleCancel
            }
            disabled={
              saving
            }
          >

            Cancel

          </button>

          <button
            className="
              btn
              btn-warning
            "
            onClick={
              handleSaveDraft
            }
            disabled={
              saving
            }
          >

            {saving ? (

              <>
                <span
                  className="
                    spinner-border
                    spinner-border-sm
                    me-2
                  "
                />

                Updating...

              </>

            ) : (

              <>
                <i className="fas fa-save me-2" />

                Update Claim

              </>

            )}

          </button>

          <button
            className="
              btn
              btn-primary
            "
            onClick={
              handleSubmit
            }
            disabled={
              saving
            }
          >

            <i className="fas fa-paper-plane me-2" />

            Submit

          </button>

        </div>

      </div>

    </div>

  );

}